import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomerService } from '../../../Services/customer.service';
import { MaintenanceService, MaintenanceMaterialPayload } from '../../../Services/maintenance.service';
import { UserService } from '../../../Services/user.service';
import { AuthService } from '../../../api-authorization/authorize.service';
import { Customer, CustomerHourlyRate } from '../../../Models/customer.model';
import { Equipment } from '../../../Models/equipment.model';
import { MaintenanceRecurrenceType, MaintenanceStatus, ProblemEntry, User } from '../../../Models/maintenance.model';
import { jsPDF } from 'jspdf';
import { drawPdfHeader, drawSectionTitle } from '../shared/pdf-branding.util';
import { environment } from '../../../environments/environment';

export interface PhotoItem {
  dataUrl: string;
  name: string;
  file?: File;
}

export interface AttachmentItem {
  dataUrl: string;
  name: string;
  file?: File;
  type: string; // mime type
}

export interface MaterialRow {
  name: string;
  quantity: number;
  unit: string;
  unitPrice?: number;
}

export interface RegieLineDraft {
  customerHourlyRateId?: number | null;
  label: string;
  hourlyRate: number;
  hours: number;
  isCustomerAcknowledged?: boolean;
}

const SETTINGS_KEY = 'service-call-settings';

@Component({
  selector: 'app-service-call-form',
  templateUrl: './service-call-form.component.html',
  styleUrls: ['./service-call-form.component.css']
})
export class ServiceCallFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('techCanvas') techCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('customerCanvas') customerCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('photoInput') photoInput!: ElementRef<HTMLInputElement>;
  @ViewChild('attachmentInput') attachmentInput!: ElementRef<HTMLInputElement>;

  customer: Customer | null = null;
  users: User[] = [];
  loading = true;
  saving = false;
  wizardMode = true;
  step = 0;
  stepLabels = ['Auftrag', 'Probleme', 'Arbeiten', 'Material', 'Fotos', 'Abschluss'];
  validationErrors: string[] = [];
  stepValidated: boolean[] = [false, false, false, false, false, false];

  // Step 0: Auftragsinformationen + Ist-Zustand + Zeiten
  title = '';
  selectedEquipmentId: number | null = null;
  serviceDate: Date = new Date();
  assignedUserIds: string[] = [];
  initialSituation = ''; // Ist-Zustand / Grund des Einsatzes

  // Time tracking: 4 timestamps
  departureFromOffice = '';
  arrivalTime = '';
  departureTime = '';
  returnToOffice = '';

  // Calculated durations (auto-computed)
  get durationHours(): number {
    return this.calcWorkDuration().h;
  }
  get durationMinutes(): number {
    return this.calcWorkDuration().m;
  }
  get travelHours(): number {
    return this.calcTravelDuration().h;
  }
  get travelMinutes(): number {
    return this.calcTravelDuration().m;
  }

  get hasAnyTimeEntry(): boolean {
    return !!(this.departureFromOffice || this.arrivalTime || this.departureTime || this.returnToOffice);
  }

  // Step 1: Probleme & Lösungen
  problems: ProblemEntry[] = [];
  newProblemDesc = '';
  newProblemSolution = '';

  // Step 2: Arbeiten
  workItems: string[] = [];
  newWorkItem = '';
  description = '';

  // Step 3: Material
  materials: MaterialRow[] = [];
  newMat: MaterialRow = { name: '', quantity: 1, unit: 'Stk', unitPrice: undefined };
  isCommerciallyRelevant = false;

  get effectiveCommerciallyRelevant(): boolean {
    return this.isCommerciallyRelevant
      || this.regieLines.length > 0
      || this.hasPricedMaterial;
  }

  get hasPricedMaterial(): boolean {
    return this.materials.some(mat => Number(mat.unitPrice) > 0);
  }

  // Step 4: Fotos & Anlagen
  photos: PhotoItem[] = [];
  attachments: AttachmentItem[] = [];

  // Step 5: Unterschrift
  techSigneeName = '';
  customerSigneeName = '';
  customerSignatureEnabled = true;
  customerAcknowledged = false;

  // Regiestunden
  regieHours = 0;
  regieMinutes = 0;
  regieLines: RegieLineDraft[] = [];
  newRegieRateId: number | null = null;
  newRegieLabel = '';
  newRegieRate = 0;
  newRegieHours = 1;

  readonly titleSuggestions = [
    'Störung behoben',
    'Sicherung getauscht',
    'Fehlersuche und Instandsetzung',
    'Programmierarbeiten durchgeführt',
    'Kundenunterstützung vor Ort'
  ];
  readonly situationSuggestions = [
    'Kunde meldet Störung an der Anlage.',
    'Anlage war bei Ankunft teilweise außer Betrieb.',
    'Fehlerbild wurde gemeinsam mit dem Kunden aufgenommen.',
    'Wiederkehrende Störung laut Kundenmeldung.'
  ];
  readonly workSuggestions = [
    'Fehleranalyse durchgeführt',
    'Sicherung geprüft und getauscht',
    'Klemmen nachgezogen',
    'Funktionstest durchgeführt',
    'Anlage gemeinsam mit Kunden getestet',
    'Softwareanpassung durchgeführt'
  ];
  readonly problemSuggestions = [
    'Sicherung ausgelöst',
    'Kommunikationsfehler zur Steuerung',
    'Lose Klemme festgestellt',
    'Bauteil defekt',
    'Fehlparametrierung festgestellt'
  ];
  readonly solutionSuggestions = [
    'Bauteil getauscht und Funktion geprüft',
    'Parameter angepasst und Testlauf durchgeführt',
    'Verbindung geprüft und Fehler behoben',
    'Kunde über weiteres Vorgehen informiert'
  ];
  readonly materialSuggestions = [
    'Sicherung',
    'Relais',
    'Klemme',
    'Leitung',
    'Netzteil',
    'Schütz'
  ];

  private techCtx: CanvasRenderingContext2D | null = null;
  private techDrawing = false;
  techSigned = false;

  private custCtx: CanvasRenderingContext2D | null = null;
  private custDrawing = false;
  custSigned = false;

  // Saved signature
  savedSignatureUrl: string | null = null;
  useSavedSignature = false;
  private savedSignatureDataUrl: string | null = null;

  // --- Edit mode (vorhandenen Einsatz bearbeiten) ---
  editMaintenanceId: number | null = null;
  private existingMaterialIds: number[] = [];
  serverPhotos: { id: number; fileName: string; url: string }[] = [];
  existingTechSignatureDataUrl: string | null = null;
  existingCustomerSignatureDataUrl: string | null = null;
  private loadedStatus: MaintenanceStatus | null = null;
  private loadedCompletedOn: string | null = null;
  private loadedStartedOn: string | null = null;
  private originalRegieSnapshot = '';
  private regieChangedInEdit = false;

  get isEdit(): boolean {
    return this.editMaintenanceId !== null;
  }

  /** Gibt es überhaupt eine gültige Techniker-Unterschrift (frisch, gespeichert oder vorhanden)? */
  get hasTechSignature(): boolean {
    return this.techSigned
      || (this.useSavedSignature && !!this.savedSignatureDataUrl)
      || (this.isEdit && !!this.existingTechSignatureDataUrl);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private maintenanceService: MaintenanceService,
    private userService: UserService,
    private authService: AuthService,
    private snackBar: MatSnackBar
  ) {
    this.loadSettings();
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const editId = +params['id'];
      if (editId) {
        this.loadForEdit(editId);
        return;
      }
      const id = +params['customerId'];
      if (id) {
        this.customerService.getCustomer(id).subscribe({
          next: c => {
            this.customer = c;
            this.selectedEquipmentId = this.getDefaultEquipmentId(c);
            this.prefillDefaultRegieRate();
            this.loading = false;
          },
          error: () => {
            this.snackBar.open('Kunde nicht gefunden', 'OK', { duration: 3000 });
            this.router.navigate(['/wartung/kunden']);
          }
        });
      }
    });
    this.userService.getUsers().subscribe(u => this.users = u ?? []);
    this.loadSavedSignature();
  }

  /** Lädt einen bestehenden Einsatz zum Bearbeiten und befüllt das Formular. */
  private loadForEdit(id: number): void {
    this.editMaintenanceId = id;
    this.maintenanceService.getMaintenance(id).subscribe({
      next: m => {
        this.prefillFromMaintenance(m);
        const custId = m.equipment?.customerId;
        if (custId) {
          this.customerService.getCustomer(custId).subscribe({
            next: c => {
              this.customer = c;
              this.prefillDefaultRegieRate();
              this.loading = false;
            },
            error: () => { this.loading = false; }
          });
        } else {
          this.loading = false;
        }
      },
      error: () => {
        this.snackBar.open('Einsatz nicht gefunden', 'OK', { duration: 3000 });
        this.router.navigate(['/wartung']);
      }
    });

    this.maintenanceService.getServiceCallPhotos(id).subscribe({
      next: p => this.serverPhotos = (p ?? []).map(x => ({ id: x.id, fileName: x.fileName, url: x.url })),
      error: () => this.serverPhotos = []
    });
  }

  private prefillFromMaintenance(m: any): void {
    this.title = m.title ?? '';
    this.selectedEquipmentId = m.equipmentId ?? null;
    this.serviceDate = m.scheduledDate ? new Date(m.scheduledDate) : new Date();
    this.assignedUserIds = (m.assignedUsers?.length ? m.assignedUsers.map((u: any) => u.id) : (m.assignedUserIds ?? [])) as string[];
    this.initialSituation = m.initialSituation ?? '';
    this.departureFromOffice = m.departureFromOffice ?? '';
    this.arrivalTime = m.arrivalTime ?? '';
    this.departureTime = m.departureTime ?? '';
    this.returnToOffice = m.returnToOffice ?? '';
    this.problems = (m.problems ?? []).map((p: any) => ({ description: p.description ?? '', solution: p.solution ?? '' }));
    this.workItems = [...(m.workItems ?? [])];
    this.description = m.notes ?? '';
    this.materials = (m.materials ?? []).map((mat: any) => ({
      name: mat.name, quantity: mat.quantity, unit: mat.unit, unitPrice: mat.unitPrice ?? undefined
    }));
    this.existingMaterialIds = (m.materials ?? []).map((mat: any) => mat.id);
    this.regieLines = (m.regieLines ?? []).map((r: any) => ({
      customerHourlyRateId: r.customerHourlyRateId ?? null,
      label: r.label, hourlyRate: r.hourlyRate, hours: r.hours,
      isCustomerAcknowledged: r.isCustomerAcknowledged
    }));
    this.originalRegieSnapshot = this.regieSnapshot();
    this.customerAcknowledged = !!m.customerAcknowledged;
    this.techSigneeName = m.techSigneeName ?? '';
    this.customerSigneeName = m.customerSigneeName ?? '';
    this.customerSignatureEnabled = m.customerSignatureEnabled ?? true;
    this.isCommerciallyRelevant = !!m.isCommerciallyRelevant;
    this.existingTechSignatureDataUrl = m.techSignatureDataUrl ?? null;
    this.existingCustomerSignatureDataUrl = m.customerSignatureDataUrl ?? null;
    this.loadedStatus = m.status ?? null;
    this.loadedCompletedOn = m.completedOn ?? null;
    this.loadedStartedOn = m.startedOn ?? null;
  }

  private regieSnapshot(): string {
    return JSON.stringify(this.regieLines.map(l => ({ l: l.label, h: l.hours, r: l.hourlyRate })));
  }

  ngAfterViewInit(): void {
    if (!this.wizardMode) {
      setTimeout(() => {
        this.initTechSignature();
        if (this.customerSignatureEnabled) {
          this.initCustomerSignature();
        }
      }, 300);
    }
  }

  ngOnDestroy(): void {
    this.persistSettings();
  }

  // --- Settings Persistence ---
  private loadSettings(): void {
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (s.customerSignatureEnabled !== undefined) this.customerSignatureEnabled = s.customerSignatureEnabled;
      if (s.techSigneeName) this.techSigneeName = s.techSigneeName;
      if (s.wizardMode !== undefined) this.wizardMode = s.wizardMode;
    } catch { }
  }

  private persistSettings(): void {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify({
        customerSignatureEnabled: this.customerSignatureEnabled,
        techSigneeName: this.techSigneeName,
        wizardMode: this.wizardMode
      }));
    } catch { }
  }

  // --- Signature Canvas ---
  private techListenersAttached = false;
  private custListenersAttached = false;

  private initCanvas(
    canvasRef: ElementRef<HTMLCanvasElement>,
    setCtx: (ctx: CanvasRenderingContext2D) => void,
    setDrawing: (d: boolean) => void,
    getDrawing: () => boolean,
    setSigned: () => void,
    listenersKey: 'tech' | 'cust'
  ): void {
    if (!canvasRef) return;
    const canvas = canvasRef.nativeElement;
    if (!canvas || !canvas.offsetWidth) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setCtx(ctx);

    // Only attach listeners once per canvas element
    const alreadyAttached = listenersKey === 'tech' ? this.techListenersAttached : this.custListenersAttached;
    if ((canvas as any).__sigListenersAttached) return;
    (canvas as any).__sigListenersAttached = true;
    if (listenersKey === 'tech') this.techListenersAttached = true;
    else this.custListenersAttached = true;

    const getPos = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const getTouchPos = (e: TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };

    canvas.addEventListener('mousedown', (e) => {
      setDrawing(true);
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    });
    canvas.addEventListener('mousemove', (e) => {
      if (!getDrawing()) return;
      const pos = getPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      setSigned();
    });
    canvas.addEventListener('mouseup', () => setDrawing(false));
    canvas.addEventListener('mouseleave', () => setDrawing(false));
    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      setDrawing(true);
      const pos = getTouchPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }, { passive: false });
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (!getDrawing()) return;
      const pos = getTouchPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      setSigned();
    }, { passive: false });
    canvas.addEventListener('touchend', () => setDrawing(false));
  }

  initTechSignature(): void {
    setTimeout(() => {
      if (!this.techCanvas) return;
      this.initCanvas(
        this.techCanvas,
        (ctx) => this.techCtx = ctx,
        (d) => this.techDrawing = d,
        () => this.techDrawing,
        () => this.techSigned = true,
        'tech'
      );
    }, 150);
  }

  initCustomerSignature(): void {
    setTimeout(() => {
      if (!this.customerCanvas) return;
      this.initCanvas(
        this.customerCanvas,
        (ctx) => this.custCtx = ctx,
        (d) => this.custDrawing = d,
        () => this.custDrawing,
        () => this.custSigned = true,
        'cust'
      );
    }, 150);
  }

  clearTechSignature(): void {
    if (!this.techCtx || !this.techCanvas) return;
    const canvas = this.techCanvas.nativeElement;
    this.techCtx.clearRect(0, 0, canvas.width, canvas.height);
    this.techSigned = false;
  }

  clearCustomerSignature(): void {
    if (!this.custCtx || !this.customerCanvas) return;
    const canvas = this.customerCanvas.nativeElement;
    this.custCtx.clearRect(0, 0, canvas.width, canvas.height);
    this.custSigned = false;
  }

  private loadSavedSignature(): void {
    this.authService.getSignature().subscribe({
      next: (res: { hasSignature: boolean; url: string | null }) => {
        if (res.hasSignature && res.url) {
          const base = environment.apiBaseUrl.replace(/\/api\/?$/, '');
          this.savedSignatureUrl = base + res.url;
        }
      },
      error: () => {}
    });
  }

  applySavedSignature(): void {
    if (!this.savedSignatureUrl) return;
    this.useSavedSignature = true;
    this.techSigned = true;

    // Load image and convert to dataUrl for PDF generation
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        this.savedSignatureDataUrl = canvas.toDataURL('image/png');
      }
    };
    img.onerror = () => {
      this.techSigned = false;
      this.snackBar.open('Gespeicherte Unterschrift konnte nicht geladen werden', 'OK', { duration: 3500 });
    };
    img.src = this.savedSignatureUrl;
  }

  getTechSignatureDataUrl(): string | null {
    if (this.techSigned && this.techCanvas) {
      return this.techCanvas.nativeElement.toDataURL('image/png');
    }
    if (this.useSavedSignature && this.savedSignatureDataUrl) {
      return this.savedSignatureDataUrl;
    }
    // Im Bearbeiten-Modus die bereits vorhandene Unterschrift behalten.
    if (this.isEdit && this.existingTechSignatureDataUrl) {
      return this.existingTechSignatureDataUrl;
    }
    return null;
  }

  // --- Navigation ---
  get equipments(): Equipment[] {
    return this.customer?.equipments ?? [];
  }

  get hourlyRates(): CustomerHourlyRate[] {
    return this.customer?.hourlyRates ?? [];
  }

  nextStep(): void {
    if (!this.validateCurrentStep()) return;
    if (this.step < this.stepLabels.length - 1) {
      this.step++;
      this.onStepEnter();
    }
  }

  prevStep(): void {
    if (this.step > 0) this.step--;
  }

  goToStep(s: number): void {
    if (s > this.step && !this.validateCurrentStep()) return;
    this.step = s;
    this.onStepEnter();
  }

  private onStepEnter(): void {
    if (this.step === this.stepLabels.length - 1) {
      // Signature step
      setTimeout(() => {
        this.initTechSignature();
        if (this.customerSignatureEnabled) {
          this.initCustomerSignature();
        }
      }, 150);
    }
  }

  toggleWizardMode(): void {
    this.wizardMode = !this.wizardMode;
    this.persistSettings();
    if (!this.wizardMode) {
      // Init signatures when switching to full form mode
      setTimeout(() => {
        this.initTechSignature();
        if (this.customerSignatureEnabled) {
          this.initCustomerSignature();
        }
      }, 200);
    }
  }

  toggleCustomerSignature(): void {
    this.persistSettings();
    if (this.customerSignatureEnabled && (!this.wizardMode || this.step === this.stepLabels.length - 1)) {
      this.initCustomerSignature();
    }
  }

  // --- Validation ---
  validateCurrentStep(): boolean {
    this.validationErrors = [];
    this.stepValidated[this.step] = true;
    switch (this.step) {
      case 0:
        if (!this.title.trim()) this.validationErrors.push('Titel ist erforderlich.');
        if (!this.selectedEquipmentId) this.validationErrors.push('Anlage / Verteiler muss ausgewählt werden.');
        break;
      case 1: break; // problems optional
      case 2: break; // work items optional
      case 3: break; // material optional
      case 4: break; // photos optional
      case 5:
        if (!this.hasTechSignature) this.validationErrors.push('Techniker-Unterschrift ist erforderlich.');
        break;
    }
    return this.validationErrors.length === 0;
  }

  validateAll(): string[] {
    const errors: string[] = [];
    if (!this.title.trim()) errors.push('Titel ist erforderlich.');
    if (!this.selectedEquipmentId) errors.push('Anlage / Verteiler muss ausgewählt werden.');
    if (!this.hasTechSignature) errors.push('Techniker-Unterschrift ist erforderlich.');
    // Kundenbestätigung nur beim erstmaligen Anlegen erzwingen; beim Bearbeiten
    // wurde sie i.d.R. bereits eingeholt.
    if (!this.isEdit && this.customerSignatureEnabled && !this.customerAcknowledged) {
      errors.push('Der Kunde muss die Bestätigung über Stunden und Arbeiten anhaken.');
    }
    return errors;
  }

  get canProceed(): boolean {
    switch (this.step) {
      case 0: return !!this.title.trim() && !!this.selectedEquipmentId;
      default: return true;
    }
  }

  // --- Step 0: Time Calculations ---
  private parseTime(t: string): number | null {
    if (!t) return null;
    const parts = t.split(':');
    if (parts.length !== 2) return null;
    return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
  }

  private calcWorkDuration(): { h: number; m: number } {
    const arr = this.parseTime(this.arrivalTime);
    const dep = this.parseTime(this.departureTime);
    if (arr === null || dep === null) return { h: 0, m: 0 };
    let diff = dep - arr;
    if (diff < 0) diff += 24 * 60;
    return { h: Math.floor(diff / 60), m: diff % 60 };
  }

  private calcTravelDuration(): { h: number; m: number } {
    const depOff = this.parseTime(this.departureFromOffice);
    const arr = this.parseTime(this.arrivalTime);
    const dep = this.parseTime(this.departureTime);
    const ret = this.parseTime(this.returnToOffice);
    let total = 0;
    if (depOff !== null && arr !== null) {
      let d = arr - depOff;
      if (d < 0) d += 24 * 60;
      total += d;
    }
    if (dep !== null && ret !== null) {
      let d = ret - dep;
      if (d < 0) d += 24 * 60;
      total += d;
    }
    return { h: Math.floor(total / 60), m: total % 60 };
  }

  private calcTotalDuration(): { h: number; m: number } | null {
    const depOff = this.parseTime(this.departureFromOffice);
    const ret = this.parseTime(this.returnToOffice);
    if (depOff === null || ret === null) return null;

    let diff = ret - depOff;
    if (diff < 0) diff += 24 * 60;
    return { h: Math.floor(diff / 60), m: diff % 60 };
  }

  get durationText(): string {
    const h = this.durationHours;
    const m = this.durationMinutes;
    if (h === 0 && m === 0) return '–';
    return `${h}h ${m}min`;
  }

  get travelTimeText(): string {
    const h = this.travelHours;
    const m = this.travelMinutes;
    if (h === 0 && m === 0) return '–';
    return `${h}h ${m}min`;
  }

  get totalTimeText(): string {
    const total = this.calcTotalDuration();
    if (!total) return '–';
    if (total.h === 0 && total.m === 0) return '0min';
    return `${total.h}h ${total.m}min`;
  }

  get regieText(): string {
    if (this.regieLines.length > 0) {
      return `${this.getRegieHoursTotal().toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}h · ${this.getRegieTotal().toLocaleString('de-AT', { style: 'currency', currency: 'EUR' })}`;
    }
    const h = this.regieHours || 0;
    const m = this.regieMinutes || 0;
    if (h === 0 && m === 0) return '–';
    return `${h}h ${m}min`;
  }

  getRegieHoursTotal(): number {
    return this.regieLines.reduce((sum, line) => sum + (Number(line.hours) || 0), 0);
  }

  getRegieTotal(): number {
    return this.regieLines.reduce((sum, line) => sum + ((Number(line.hours) || 0) * (Number(line.hourlyRate) || 0)), 0);
  }

  private getRegieLinesForSave(): RegieLineDraft[] {
    return this.regieLines.map(line => ({
      ...line,
      isCustomerAcknowledged: this.customerAcknowledged ? true : !!line.isCustomerAcknowledged
    }));
  }

  onRegieRateSelected(rateId: number | null): void {
    const rate = this.hourlyRates.find(r => r.id === rateId);
    if (!rate) return;
    this.newRegieLabel = rate.label;
    this.newRegieRate = rate.hourlyRate;
  }

  addRegieLine(): void {
    const label = this.newRegieLabel.trim();
    const hours = Number(this.newRegieHours);
    const hourlyRate = Number(this.newRegieRate);
    if (!label || hours <= 0 || hourlyRate < 0) return;

    this.regieLines.push({
      customerHourlyRateId: this.newRegieRateId,
      label,
      hourlyRate,
      hours,
      isCustomerAcknowledged: false
    });
    this.isCommerciallyRelevant = true;
    this.prefillDefaultRegieRate();
    this.newRegieHours = 1;
    this.onRegieChanged();
  }

  removeRegieLine(index: number): void {
    this.regieLines.splice(index, 1);
    this.onRegieChanged();
  }

  /** Im Bearbeiten-Modus: geänderte Regiezeilen setzen die Kundenbestätigung zurück. */
  private onRegieChanged(): void {
    if (this.isEdit && this.regieSnapshot() !== this.originalRegieSnapshot) {
      this.regieChangedInEdit = true;
      if (this.customerAcknowledged) {
        this.customerAcknowledged = false;
        this.snackBar.open('Regiezeilen geändert – Kundenbestätigung bitte ggf. erneut einholen.', 'OK', { duration: 4000 });
      }
    }
  }

  private prefillDefaultRegieRate(): void {
    const rate = this.hourlyRates.find(r => r.isDefault) ?? this.hourlyRates[0];
    if (!rate) {
      this.newRegieRateId = null;
      this.newRegieLabel = '';
      this.newRegieRate = 0;
      return;
    }

    this.newRegieRateId = rate.id;
    this.newRegieLabel = rate.label;
    this.newRegieRate = rate.hourlyRate;
  }

  private getDefaultEquipmentId(customer: Customer): number | null {
    const equipments = customer.equipments ?? [];
    return equipments.find(e => e.name === 'Gesamtanlage')?.id ?? equipments[0]?.id ?? null;
  }

  // --- Step 1: Probleme ---
  addProblem(): void {
    if (!this.newProblemDesc.trim()) return;
    this.problems.push({ description: this.newProblemDesc.trim(), solution: this.newProblemSolution.trim() });
    this.newProblemDesc = '';
    this.newProblemSolution = '';
  }

  removeProblem(i: number): void {
    this.problems.splice(i, 1);
  }

  // --- Step 2: Work Items ---
  addWorkItem(): void {
    if (!this.newWorkItem.trim()) return;
    this.workItems.push(this.newWorkItem.trim());
    this.newWorkItem = '';
  }

  // --- Step 3: Material ---
  addMaterial(): void {
    if (!this.newMat.name.trim()) return;
    const unitPrice = Number(this.newMat.unitPrice) || 0;
    this.materials.push({ ...this.newMat, unitPrice: Math.max(0, unitPrice) });
    if (unitPrice > 0) {
      this.isCommerciallyRelevant = true;
    }
    this.newMat = { name: '', quantity: 1, unit: 'Stk', unitPrice: undefined };
  }

  removeMaterial(i: number): void {
    this.materials.splice(i, 1);
  }

  // --- Step 4: Fotos & Anlagen ---
  triggerPhotoInput(): void {
    this.photoInput?.nativeElement?.click();
  }

  triggerAttachmentInput(): void {
    this.attachmentInput?.nativeElement?.click();
  }

  onPhotosSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files[i];
      const reader = new FileReader();
      reader.onload = () => {
        this.photos.push({ dataUrl: reader.result as string, name: file.name, file });
      };
      reader.readAsDataURL(file);
    }
    input.value = '';
  }

  onAttachmentsSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files[i];
      const reader = new FileReader();
      reader.onload = () => {
        this.attachments.push({ dataUrl: reader.result as string, name: file.name, file, type: file.type });
      };
      reader.readAsDataURL(file);
    }
    input.value = '';
  }

  removePhoto(i: number): void {
    this.photos.splice(i, 1);
  }

  /** Im Bearbeiten-Modus: bereits gespeichertes Foto sofort vom Server löschen. */
  deleteServerPhoto(photo: { id: number; fileName: string; url: string }): void {
    if (!this.editMaintenanceId) return;
    this.maintenanceService.deleteServiceCallPhoto(this.editMaintenanceId, photo.id).subscribe({
      next: () => this.serverPhotos = this.serverPhotos.filter(p => p.id !== photo.id),
      error: () => this.snackBar.open('Foto konnte nicht gelöscht werden', 'OK', { duration: 3000 })
    });
  }

  serverPhotoUrl(url: string): string {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    const base = environment.apiBaseUrl.replace(/\/api\/?$/, '');
    return url.startsWith('/') ? base + url : `${base}/${url}`;
  }

  removeAttachment(i: number): void {
    this.attachments.splice(i, 1);
  }

  getAttachmentIcon(type: string): string {
    if (type === 'application/pdf') return 'picture_as_pdf';
    if (type.startsWith('image/')) return 'image';
    return 'attach_file';
  }

  // --- Save & PDF ---
  private getTodayDateString(): string {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }

  private getScheduledDateIso(): string {
    const d = this.serviceDate instanceof Date && !isNaN(this.serviceDate.getTime()) ? this.serviceDate : new Date();
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return new Date(`${year}-${month}-${day}T08:00:00`).toISOString();
  }

  saveAndExport(): void {
    if (this.saving) return;

    // Validate all fields
    this.stepValidated = [true, true, true, true, true, true];
    const errors = this.validateAll();
    if (errors.length > 0) {
      this.validationErrors = errors;
      this.snackBar.open(errors.join(' '), 'OK', { duration: 5000 });
      return;
    }

    this.saving = true;
    this.validationErrors = [];

    const techSignatureDataUrl = this.getTechSignatureDataUrl();
    const customerSignatureDataUrl = this.custSigned && this.customerCanvas
      ? this.customerCanvas.nativeElement.toDataURL('image/png')
      : (this.isEdit ? this.existingCustomerSignatureDataUrl : null);

    if (!techSignatureDataUrl) {
      this.saving = false;
      this.validationErrors = ['Techniker-Unterschrift fehlt oder wird noch geladen. Bitte erneut versuchen.'];
      this.snackBar.open(this.validationErrors[0], 'OK', { duration: 4500 });
      return;
    }

    const nowIso = new Date().toISOString();
    const payload: any = {
      equipmentId: this.selectedEquipmentId,
      title: this.title.trim(),
      scheduledDate: this.getScheduledDateIso(),
      status: this.isEdit ? (this.loadedStatus ?? MaintenanceStatus.Completed) : MaintenanceStatus.Completed,
      completedOn: this.isEdit ? (this.loadedCompletedOn ?? nowIso) : nowIso,
      startedOn: this.isEdit ? (this.loadedStartedOn ?? nowIso) : nowIso,
      notes: this.description || '',
      assignedUserIds: this.assignedUserIds.length > 0 ? this.assignedUserIds : [],
      recurrenceType: MaintenanceRecurrenceType.None,
      notificationsEnabled: false,
      notificationLeadTimeDays: 0,
      progressPercent: 100,
      durationHours: this.durationHours,
      durationMinutes: this.durationMinutes,
      travelHours: this.travelHours,
      travelMinutes: this.travelMinutes,
      departureFromOffice: this.departureFromOffice || null,
      arrivalTime: this.arrivalTime || null,
      departureTime: this.departureTime || null,
      returnToOffice: this.returnToOffice || null,
      initialSituation: this.initialSituation || '',
      problems: this.problems,
      workItems: this.workItems,
      regieHours: Math.floor(this.getRegieHoursTotal()),
      regieMinutes: Math.round((this.getRegieHoursTotal() % 1) * 60),
      regieLines: this.getRegieLinesForSave(),
      customerAcknowledged: this.customerAcknowledged,
      techSigneeName: this.techSigneeName || null,
      techSignatureDataUrl,
      customerSigneeName: this.customerSignatureEnabled ? (this.customerSigneeName || null) : null,
      customerSignatureDataUrl: this.customerSignatureEnabled ? customerSignatureDataUrl : null,
      customerSignatureEnabled: this.customerSignatureEnabled,
      isCommerciallyRelevant: this.effectiveCommerciallyRelevant
    };

    const request$ = this.isEdit && this.editMaintenanceId
      ? this.maintenanceService.updateMaintenance(this.editMaintenanceId, payload)
      : this.maintenanceService.createMaintenance(payload);

    request$.subscribe({
      next: (saved: any) => {
        const id = this.isEdit ? this.editMaintenanceId! : saved.id;
        this.syncChildrenAndFinish(id);
      },
      error: (err: any) => this.handleSaveError(err)
    });
  }

  /** Material-/Foto-Synchronisierung nach Create/Update, danach PDF + Navigation. */
  private syncChildrenAndFinish(id: number): void {
    this.saveServiceCallData(id);

    // Beim Bearbeiten vorhandene Materialien ersetzen (löschen, dann neu anlegen).
    const deletePromises = this.isEdit
      ? this.existingMaterialIds.map(mid => this.maintenanceService.deleteMaterial(id, mid).toPromise())
      : [];

    Promise.all(deletePromises).then(() => {
      const matPromises = this.materials.map(mat =>
        this.maintenanceService.addMaterial(id, {
          name: mat.name,
          quantity: mat.quantity,
          unit: mat.unit,
          unitPrice: mat.unitPrice || 0,
          description: '',
          isPacked: false
        }).toPromise()
      );

      const photoPromises = this.photos.map(photo =>
        this.maintenanceService.uploadServiceCallPhoto(id, this.dataUrlToBlob(photo.dataUrl), photo.name).toPromise()
      );

      const attachmentPromises = this.attachments.map(att =>
        this.maintenanceService.uploadServiceCallPhoto(id, this.dataUrlToBlob(att.dataUrl), att.name).toPromise()
      );

      return Promise.all([...matPromises, ...photoPromises, ...attachmentPromises]);
    }).then(() => {
      this.existingMaterialIds = [];
      this.generatePDF(id);
      this.saving = false;
      this.snackBar.open(this.isEdit ? 'Einsatz aktualisiert & PDF erstellt' : 'Einsatz gespeichert & PDF erstellt', 'OK', { duration: 4000 });
      this.router.navigate(['/wartung/auftrag', id]);
    }).catch((err) => {
      this.generatePDF(id);
      this.saving = false;
      const msg = err?.error?.message || err?.message || 'Unbekannter Fehler beim Anhängen von Material/Fotos';
      this.snackBar.open(`Gespeichert, aber: ${msg}`, 'OK', { duration: 6000 });
      this.router.navigate(['/wartung/auftrag', id]);
    });
  }

  private handleSaveError(err: any): void {
    this.saving = false;
    let msg: string;
    if (err?.status === 0) {
      msg = 'Server nicht erreichbar. Läuft der Backend-Server?';
    } else if (err?.status === 500) {
      const raw = typeof err?.error === 'string' ? err.error : '';
      const match = raw.match(/SqlException:\s*([^<]+)/);
      msg = match ? match[1].trim() : `Interner Serverfehler (500)`;
    } else {
      const detail = err?.error?.message || (typeof err?.error === 'string' ? err.error : null) || err?.message || 'Unbekannter Fehler';
      msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
    }
    this.validationErrors = [`Speichern fehlgeschlagen: ${msg}`];
    this.snackBar.open(`Fehler: ${msg}`, 'OK', { duration: 6000 });
  }

  private saveServiceCallData(maintenanceId: number): void {
    try {
      const data: any = {
        description: this.description,
        initialSituation: this.initialSituation,
        problems: this.problems,
        workItems: this.workItems,
        durationHours: this.durationHours,
        durationMinutes: this.durationMinutes,
        travelHours: this.travelHours,
        travelMinutes: this.travelMinutes,
        departureFromOffice: this.departureFromOffice || null,
        arrivalTime: this.arrivalTime || null,
        departureTime: this.departureTime || null,
        returnToOffice: this.returnToOffice || null,
        regieLines: this.getRegieLinesForSave(),
        techSigneeName: this.techSigneeName,
        customerSigneeName: this.customerSigneeName,
        customerSignatureEnabled: this.customerSignatureEnabled,
        photos: this.photos.map(photo => ({ dataUrl: photo.dataUrl, name: photo.name })),
      };
      if (this.techSigned) {
        const sigUrl = this.getTechSignatureDataUrl();
        if (sigUrl) data.techSignatureDataUrl = sigUrl;
      }
      if (this.custSigned && this.customerCanvas) {
        data.signatureDataUrl = this.customerCanvas.nativeElement.toDataURL('image/png');
      }
      localStorage.setItem(`service-call-${maintenanceId}`, JSON.stringify(data));

      // Also save tech signature under user-specific key for reuse in report dialog
      if (this.assignedUserIds.length > 0 && this.techSigned) {
        const sigData = {
          name: this.techSigneeName,
          signatureDataUrl: this.getTechSignatureDataUrl() || ''
        };
        localStorage.setItem(`technician-sig-${this.assignedUserIds[0]}`, JSON.stringify(sigData));
      }
    } catch (e) {
      console.warn('Could not save service call data to localStorage:', e);
    }
  }

  private async generatePDF(maintenanceId: number): Promise<void> {
    try {
      const doc = new jsPDF();
      const pw = doc.internal.pageSize.getWidth();
      const serviceDateStr = new Date(this.getScheduledDateIso()).toLocaleDateString('de-AT');
      let y = await drawPdfHeader(doc, 'Einsatz-Report', `Einsatzdatum: ${serviceDateStr}`);

      // Info block
      y = drawSectionTitle(doc, y, 'Auftragsinformationen');
      doc.setFontSize(10);
      const techUsers = this.users.filter(u => this.assignedUserIds.includes(u.id));
      const techName = this.techSigneeName?.trim() || techUsers.map(u => u.userName).join(', ') || '–';
      const info: [string, string][] = [
        ['Kunde:', this.customer?.name ?? '–'],
        ['Adresse:', this.customer?.address ?? '–'],
        ['Auftrag:', this.title],
        ['Anlage:', this.equipments.find(e => e.id === this.selectedEquipmentId)?.name ?? '–'],
        ['Datum:', new Date(this.getScheduledDateIso()).toLocaleDateString('de-AT')],
        ['Techniker:', techName],
        ...(this.departureFromOffice ? [['Abfahrt Büro:', this.departureFromOffice] as [string, string]] : []),
        ...(this.arrivalTime ? [['Ankunft vor Ort:', this.arrivalTime] as [string, string]] : []),
        ...(this.departureTime ? [['Abfahrt vor Ort:', this.departureTime] as [string, string]] : []),
        ...(this.returnToOffice ? [['Rückkehr Büro:', this.returnToOffice] as [string, string]] : []),
        ['Arbeitszeit:', this.durationText],
        ['Fahrzeit:', this.travelTimeText],
        ['Regie:', this.regieText],
      ];
      for (const [label, value] of info) {
        doc.setFont('helvetica', 'bold');
        doc.text(label, 14, y);
        doc.setFont('helvetica', 'normal');
        doc.text(value, 55, y);
        y += 6;
      }
      y += 6;

      // Ist-Zustand
      if (this.initialSituation.trim()) {
        y = drawSectionTitle(doc, y, 'Ist-Zustand / Grund des Einsatzes');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(this.initialSituation, pw - 28);
        doc.text(lines, 14, y);
        y += lines.length * 5 + 6;
      }

      // Problems & Solutions
      if (this.problems.length > 0) {
        if (y > 240) { doc.addPage(); y = 20; }
        y = drawSectionTitle(doc, y, 'Festgestellte Probleme & Lösungen');
        doc.setFontSize(9);
        for (let i = 0; i < this.problems.length; i++) {
          const p = this.problems[i];
          doc.setFont('helvetica', 'bold');
          doc.text(`Problem ${i + 1}:`, 14, y);
          y += 5;
          doc.setFont('helvetica', 'normal');
          const pLines = doc.splitTextToSize(p.description, pw - 32);
          doc.text(pLines, 18, y);
          y += pLines.length * 5 + 2;
          if (p.solution.trim()) {
            doc.setFont('helvetica', 'bold');
            doc.text('Lösung:', 18, y);
            y += 5;
            doc.setFont('helvetica', 'normal');
            const sLines = doc.splitTextToSize(p.solution, pw - 36);
            doc.text(sLines, 22, y);
            y += sLines.length * 5 + 2;
          }
          y += 4;
          if (y > 270) { doc.addPage(); y = 20; }
        }
        y += 4;
      }

      // Description & Work Items
      if (this.description.trim() || this.workItems.length > 0) {
        y = drawSectionTitle(doc, y, 'Durchgeführte Arbeiten');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        if (this.description.trim()) {
          const lines = doc.splitTextToSize(this.description, pw - 28);
          doc.text(lines, 14, y);
          y += lines.length * 5 + 4;
        }
        if (this.workItems.length > 0) {
          for (let i = 0; i < this.workItems.length; i++) {
            const itemText = `${i + 1}. ${this.workItems[i]}`;
            const itemLines = doc.splitTextToSize(itemText, pw - 32);
            doc.text(itemLines, 18, y);
            y += itemLines.length * 5;
            if (y > 270) { doc.addPage(); y = 20; }
          }
        }
        y += 6;
      }

      // Materials
      if (this.regieLines.length > 0) {
        if (y > 235) { doc.addPage(); y = 20; }
        y = drawSectionTitle(doc, y, 'Regiestunden');
        doc.setFontSize(9);
        for (const line of this.regieLines) {
          const total = (line.hours * line.hourlyRate).toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
          doc.text(`${line.label}: ${line.hours.toLocaleString('de-AT')} h x ${line.hourlyRate.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' })}/h = ${total}`, 18, y);
          y += 5;
          if (y > 270) { doc.addPage(); y = 20; }
        }
        y += 6;
      }

      // Materials
      if (this.materials.length > 0) {
        if (y > 240) { doc.addPage(); y = 20; }
        y = drawSectionTitle(doc, y, 'Verwendetes Material');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        for (const mat of this.materials) {
          doc.text(`• ${mat.name} – ${mat.quantity} ${mat.unit}`, 18, y);
          y += 5;
          if (y > 270) { doc.addPage(); y = 20; }
        }
        y += 6;
      }

      // Photos
      if (this.photos.length > 0) {
        if (y > 180) { doc.addPage(); y = 20; }
        y = drawSectionTitle(doc, y, 'Fotodokumentation');
        y += 2;

        const imgW = 80;
        const imgH = 60;
        let x = 14;
        for (const photo of this.photos) {
          if (y + imgH > 270) { doc.addPage(); y = 20; x = 14; }
          try {
            doc.addImage(photo.dataUrl, 'JPEG', x, y, imgW, imgH);
          } catch {
            try { doc.addImage(photo.dataUrl, 'PNG', x, y, imgW, imgH); } catch { /* skip */ }
          }
          if (x === 14) {
            x = pw / 2 + 5;
          } else {
            x = 14;
            y += imgH + 8;
          }
        }
        if (x !== 14) y += imgH + 8;
        y += 6;
      }

      // Technician Signature
      if (y > 200) { doc.addPage(); y = 20; }
      y += 6;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Unterschrift Techniker', 14, y);
      y += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(this.techSigneeName || '–', 14, y);
      y += 4;

      if (this.techSigned) {
        const sigImg = this.getTechSignatureDataUrl();
        if (sigImg) {
          const sigW = pw - 28;
          const sigH = 35;
          doc.addImage(sigImg, 'PNG', 14, y, sigW, sigH);
          y += sigH + 4;
        }
      }
      doc.setDrawColor(200);
      doc.line(14, y, pw - 14, y);
      y += 8;

      // Customer Signature (optional)
      if (this.customerSignatureEnabled && this.custSigned) {
        if (y > 230) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Unterschrift Kunde', 14, y);
        y += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(this.customerSigneeName || '–', 14, y);
        y += 4;

        if (this.customerCanvas) {
          const sigImg = this.customerCanvas.nativeElement.toDataURL('image/png');
          const sigW = pw - 28;
          const sigH = 35;
          doc.addImage(sigImg, 'PNG', 14, y, sigW, sigH);
          y += sigH + 4;
        }
        doc.setDrawColor(200);
        doc.line(14, y, pw - 14, y);
      }

      // Footer: Erstellt am
      const ph = doc.internal.pageSize.getHeight();
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-AT')}`, 14, ph - 8);

      const filename = `Einsatz_${this.customer?.name ?? 'Report'}_${new Date().toISOString().slice(0, 10)}.pdf`;
      const pdfDataUri = doc.output('datauristring');
      const pdfBase64 = pdfDataUri.includes(',') ? pdfDataUri.split(',')[1] : pdfDataUri;
      doc.save(filename);

      // Upload PDF to server
      if (maintenanceId) {
        this.maintenanceService.uploadServiceCallReportPdf(maintenanceId, {
          fileName: filename,
          pdfBase64
        }).subscribe({
          next: () => console.log('PDF saved to server'),
          error: (err) => console.warn('PDF server upload failed:', err)
        });
      }
    } catch (e) {
      console.error('PDF generation error:', e);
    }
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const parts = dataUrl.split(',');
    const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/png';
    const binary = atob(parts[1] || '');
    const array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }

    return new Blob([array], { type: mime });
  }

  goBack(): void {
    if (this.customer) {
      this.router.navigate(['/wartung/kunden', this.customer.id]);
    } else {
      this.router.navigate(['/wartung']);
    }
  }
}
